import { useState } from 'react';
import { slugifyKey, type EditableField, type EditableSection } from '../../lib/adminTemplates';
import type { AppliesTo, FieldType } from '../../lib/fieldValue';
import { ConfirmRow } from '../ConfirmRow';
import { FieldRow } from './FieldRow';

interface SectionEditorProps {
  section: EditableSection;
  errors: Record<string, string>;
  /** Keyed by the field's index within this section (matches the `PUT` body's `fields[j]` order),
   * not by `localId` — server errors are index-addressed. */
  fieldErrors: Record<number, Record<string, string>>;
  otherSections: { localId: string; name: string }[];
  isFirst: boolean;
  isLast: boolean;
  disabled: boolean;
  onUpdate: (patch: Partial<Pick<EditableSection, 'key' | 'name' | 'isChapter'>>) => void;
  onMove: (direction: 'up' | 'down') => void;
  onRemove: () => void;
  onAddField: () => void;
  onUpdateField: (fieldLocalId: string, patch: Partial<Omit<EditableField, 'localId'>>) => void;
  onChangeFieldType: (fieldLocalId: string, type: FieldType) => void;
  onChangeFieldAppliesTo: (fieldLocalId: string, appliesTo: AppliesTo) => void;
  onMoveField: (fieldLocalId: string, direction: 'up' | 'down') => void;
  onMoveFieldToSection: (fieldLocalId: string, toSectionLocalId: string) => void;
  onRemoveField: (fieldLocalId: string) => void;
}

/** One section's editable card: name/key/chapter header, move/remove controls (remove asks for
 * confirmation when the section still has fields — WP10 spec), and its ordered `FieldRow` list. */
export function SectionEditor({
  section,
  errors,
  fieldErrors,
  otherSections,
  isFirst,
  isLast,
  disabled,
  onUpdate,
  onMove,
  onRemove,
  onAddField,
  onUpdateField,
  onChangeFieldType,
  onChangeFieldAppliesTo,
  onMoveField,
  onMoveFieldToSection,
  onRemoveField,
}: SectionEditorProps) {
  const [keyTouched, setKeyTouched] = useState(section.key.trim() !== '');
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  function onNameChange(name: string) {
    onUpdate(keyTouched ? { name } : { name, key: slugifyKey(name) });
  }

  function onKeyChange(key: string) {
    setKeyTouched(true);
    onUpdate({ key });
  }

  return (
    <section className="rounded-lg border border-border-strong bg-bg-elevated p-4" style={{ viewTransitionName: `vt-${section.localId}` }}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr_auto]">
          <label className="flex flex-col gap-1 text-sm text-text-muted">
            <span className="eyebrow">Section name</span>
            <input
              type="text"
              value={section.name}
              disabled={disabled}
              onChange={(event) => onNameChange(event.target.value)}
              className="min-h-tap rounded-md border border-border-strong bg-bg px-3 text-text disabled:opacity-50"
            />
            {errors.name && (
              <p role="alert" className="telemetry text-signal">
                {errors.name}
              </p>
            )}
          </label>
          <label className="flex flex-col gap-1 text-sm text-text-muted">
            <span className="eyebrow">Key</span>
            <input
              type="text"
              value={section.key}
              disabled={disabled}
              onChange={(event) => onKeyChange(event.target.value)}
              className="telemetry min-h-tap rounded-md border border-border-strong bg-bg px-3 text-text disabled:opacity-50"
            />
            {errors.key && (
              <p role="alert" className="telemetry text-signal">
                {errors.key}
              </p>
            )}
          </label>
          <label className="flex min-h-tap items-center gap-2 self-end text-sm text-text-muted">
            <input
              type="checkbox"
              checked={section.isChapter}
              disabled={disabled}
              onChange={(event) => onUpdate({ isChapter: event.target.checked })}
              className="size-4"
            />
            Chapter
          </label>
        </div>

        {!disabled && (
          <div className="flex shrink-0 gap-1.5">
            <button
              type="button"
              disabled={isFirst}
              onClick={() => onMove('up')}
              className="min-h-tap min-w-tap rounded-md border border-border-strong px-2 text-sm text-text-muted disabled:opacity-30"
              aria-label={`Move ${section.name || 'section'} up`}
            >
              ↑
            </button>
            <button
              type="button"
              disabled={isLast}
              onClick={() => onMove('down')}
              className="min-h-tap min-w-tap rounded-md border border-border-strong px-2 text-sm text-text-muted disabled:opacity-30"
              aria-label={`Move ${section.name || 'section'} down`}
            >
              ↓
            </button>
            <button
              type="button"
              onClick={() => (section.fields.length > 0 ? setConfirmingRemove(true) : onRemove())}
              className="min-h-tap min-w-tap rounded-md border border-border-strong px-2 text-sm text-text-muted hover:border-signal hover:text-signal"
              aria-label={`Remove ${section.name || 'section'}`}
            >
              Remove
            </button>
          </div>
        )}
      </div>

      {confirmingRemove && (
        <div className="mt-3">
          <ConfirmRow
            message={`Remove "${section.name || 'this section'}" and its ${section.fields.length} field(s)?`}
            pending={false}
            confirmLabel="Remove section"
            onConfirm={() => {
              setConfirmingRemove(false);
              onRemove();
            }}
            onCancel={() => setConfirmingRemove(false)}
          />
        </div>
      )}

      <ul className="mt-4 flex flex-col gap-3">
        {section.fields.map((field, index) => (
          <FieldRow
            key={field.localId}
            field={field}
            errors={fieldErrors[index] ?? {}}
            otherSections={otherSections}
            isFirst={index === 0}
            isLast={index === section.fields.length - 1}
            disabled={disabled}
            onUpdate={(patch) => onUpdateField(field.localId, patch)}
            onChangeType={(type) => onChangeFieldType(field.localId, type)}
            onChangeAppliesTo={(appliesTo) => onChangeFieldAppliesTo(field.localId, appliesTo)}
            onMove={(direction) => onMoveField(field.localId, direction)}
            onMoveToSection={(toSectionLocalId) => onMoveFieldToSection(field.localId, toSectionLocalId)}
            onRemove={() => onRemoveField(field.localId)}
          />
        ))}
      </ul>

      {!disabled && (
        <button
          type="button"
          onClick={onAddField}
          className="mt-3 min-h-tap rounded-md border border-border-strong px-3 text-sm font-semibold text-text-muted hover:border-accent-border hover:text-text"
        >
          Add field
        </button>
      )}
    </section>
  );
}
